"use client";

import { useEffect, useState } from "react";
import { GA_MEASUREMENT_ID, hasAnalyticsConsent } from "@/lib/gtag";

/**
 * Injects GA4 (gtag.js) imperatywnie przez useEffect, zamiast renderowania
 * <Script> w drzewie - dzięki temu komponent ZAWSZE zwraca null i nie zmienia
 * kształtu drzewa po hydratacji (co psuło reconcilera i powodowało
 * "removeChild ... parentNode is null" oraz podwójne kliknięcia w nawigacji).
 */
export default function GoogleAnalytics({
  measurementId,
}: {
  measurementId?: string;
}) {
  const id = measurementId || GA_MEASUREMENT_ID;
  const [consented, setConsented] = useState<boolean>(false);

  useEffect(() => {
    setConsented(hasAnalyticsConsent());
    const onConsent = (e: Event) => {
      const detail = (e as CustomEvent<{ decision?: string }>).detail;
      setConsented(detail?.decision === "accept");
    };
    window.addEventListener("cookie-consent", onConsent);
    return () => window.removeEventListener("cookie-consent", onConsent);
  }, []);

  useEffect(() => {
    if (!id) return;
    if (process.env.NODE_ENV === "development") return;
    if (!consented) return;
    if (document.getElementById("ga4-loader")) return;

    const loader = document.createElement("script");
    loader.id = "ga4-loader";
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(loader);

    const init = document.createElement("script");
    init.id = "ga4-init";
    init.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', '${id}', {
        anonymize_ip: true,
        send_page_view: false
      });
    `;
    document.head.appendChild(init);
  }, [id, consented]);

  return null;
}
